import React, { useEffect, useState } from "react";
import {
    formatDuration,
    timeI18n,
    TimeI18n,
} from "../../../../../../utils/format";
import { defineMessages, useIntl } from "react-intl";
import { ModuleTimeSpent } from "../../Types";
import { getModuleName } from "../../../../../Clustering/utils";
import { useData } from "../../../../../../hooks/useData";
import { calculateTotalTimeSpent } from "../utils";
import { commonMessages } from "../../../../../../utils/messages";
import Select from "../../../../../../components/Select/Select";
import "./TimeSpentByDayChart.scss";
import { ModulesListEntry } from "../../StudentDetails";

export const ALL_MODULES_SELECT_ID = "__all_modules_id__";

interface Props {
    moduleTimeSpent: ModuleTimeSpent[];
    currentModuleId: string;
    modulesList: ModulesListEntry[];
}

const TimeSpentByDayChart = ({
    moduleTimeSpent,
    currentModuleId,
    modulesList,
}: Props) => {
    const intl = useIntl();
    const data = useData();
    const [moduleId, setModuleId] = useState<string>(currentModuleId);

    const weekdays = [
        intl.formatMessage(commonMessages.monday),
        intl.formatMessage(commonMessages.tuesday),
        intl.formatMessage(commonMessages.wednesday),
        intl.formatMessage(commonMessages.thursday),
        intl.formatMessage(commonMessages.friday),
        intl.formatMessage(commonMessages.saturday),
        intl.formatMessage(commonMessages.sunday),
    ].map((day) => day.slice(0, 3));

    const timeSpent: number | undefined =
        moduleId === ALL_MODULES_SELECT_ID
            ? calculateTotalTimeSpent(moduleTimeSpent)
            : moduleTimeSpent.find((mod) => mod.id === moduleId)?.timeSpent;

    useEffect(() => {
        setModuleId(currentModuleId);
    }, [currentModuleId]);

    const weekdaysData = getWeekdaysData(moduleTimeSpent, moduleId, weekdays);
    return (
        <div className={"time-spent-by-day-chart"}>
            <Select
                className="time-spent-by-day-chart__select"
                onSelectOption={(value: string) => {
                    setModuleId(value);
                }}
                selectedOption={
                    moduleId === ALL_MODULES_SELECT_ID
                        ? intl.formatMessage(messages.allModules)
                        : getModuleName(moduleId, data)
                }
            >
                <option
                    key="all_modules"
                    value={ALL_MODULES_SELECT_ID}
                    selected={moduleId === ALL_MODULES_SELECT_ID}
                >
                    {intl.formatMessage(messages.allModules)}
                </option>
                {modulesList.map((module, i) => (
                    <option
                        key={i}
                        value={module.id}
                        selected={moduleId === module.id}
                    >
                        {module.name}
                    </option>
                ))}
            </Select>

            <div className="time-spent-by-day-chart__block">
                {
                    <p className="time-spent-by-day-chart__total-time-spent">
                        {timeSpent && timeSpent > 0
                            ? formatDuration(timeSpent, timeI18n(intl))
                            : intl.formatMessage(messages.noTimeSpent)}
                    </p>
                }

                <div className="time-spent-by-day-chart__chart">
                    <div className="time-spent-by-day-chart__chart--days">
                        {weekdaysData.map((weekday, i: number) => {
                            return <div key={i}>{weekday.day}</div>;
                        })}
                    </div>
                    <div className="time-spent-by-day-chart__chart--vertical-bars">
                        <div className="vertical-bar-container">
                            <div className="time-spent-by-day-chart__chart--vertical-bar first"></div>
                            <p>0</p>
                        </div>
                        <div className="vertical-bar-container">
                            <div className="time-spent-by-day-chart__chart--vertical-bar"></div>
                            <p>{`30\u202f${timeI18n(intl).minute}`}</p>
                        </div>
                        <div className="vertical-bar-container">
                            <div className="time-spent-by-day-chart__chart--vertical-bar"></div>
                            <p>{`60\u202f${timeI18n(intl).minute}`}</p>
                        </div>

                        {weekdaysData.map((weekday, index: number) => {
                            return weekday.time != 0
                                ? horizontalBar(
                                      weekday.time,
                                      index,
                                      timeI18n(intl)
                                  )
                                : "";
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

const horizontalBar = (time: number, index: number, timeI18n: TimeI18n) => {
    const proportion = time >= 3600 ? 1 : Math.max(0.01, time / 3600);
    const heightElement = 33;
    const top = (index + 0.5) * heightElement - 5;
    const minutes = Math.floor(Math.max(time / 60, 1));
    return (
        <div
            key={`weekday_index${index}`}
            className="time-spent-bar"
            style={{
                width: `${Math.floor(proportion * 100)}%`,
                top: `${top}px`,
            }}
        >
            <div className="time-spent-circle"></div>
            <div className="time-spent-number-container">
                <div className="time-spent-number">
                    {time < 60
                        ? `${time}\u202f${timeI18n.second}`
                        : `${minutes}\u202f${timeI18n.minute}`}
                </div>
            </div>
        </div>
    );
};

const getWeekdaysData = (
    moduleTimeSpent: ModuleTimeSpent[],
    moduleId: string,
    weekdays: string[]
) => {
    const timeSpentPerDay =
        (moduleId === ALL_MODULES_SELECT_ID
            ? moduleTimeSpent.reduce((acc, moduleData) => {
                  return acc.map(
                      (num, index) => num + moduleData.timeSpentByDay[index]
                  );
              }, Array(7).fill(0))
            : moduleTimeSpent.find((module) => module.id === moduleId)
                  ?.timeSpentByDay) || Array(7).fill(0);

    return weekdays.map((day, index) => {
        return { day: day, time: timeSpentPerDay[index] };
    });
};

export default TimeSpentByDayChart;

const messages = defineMessages({
    allModules: {
        id: "all-modules",
        defaultMessage: "All modules",
    },
    noTimeSpent: {
        id: "no-time-spent",
        defaultMessage: "No data",
    },
});
